/* ------------------------------------------------------------------
   AIBulkStudioPage.js — AI Studio → AI Bulk Post Creation
   (/dashboard/studio/bulk and /dashboard/studio/bulk/:jobId).

   Two ways in, one finish: the guided chat (AIBulkChatPage) and the
   step-by-step form (AIBulkCreatePage). A switch at the top picks one
   (?mode=chat | ?mode=form); without it the page opens in
   AI_STUDIO_DEFAULT_MODE (constants.js). Both generate through
   /api/ai/bulk/generate and land on /dashboard/studio/bulk/:jobId, which
   always shows the form page's Review → Schedule steps.

   After a sign-up from the name generators (T24, Onboarding.dc.html) the
   page opens on the 'personal' goal with the account name, vibe and topics
   prefilled (utils/onboarding.js). The prefill is read once, kept for this
   visit (so switching flows keeps it) and removed from storage.
------------------------------------------------------------------ */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ChatDotsFill, ListCheck } from 'react-bootstrap-icons';
import AIBulkCreatePage from './AIBulkCreatePage';
import AIBulkChatPage from './AIBulkChatPage';
import { AI_STUDIO_DEFAULT_MODE } from '../../../constants';
import { peekStudioPrefill, forgetStudioPrefill } from '../../../utils/onboarding';

export const STUDIO_MODES = [
  { key: 'chat', label: 'Chat', Icon: ChatDotsFill },
  { key: 'form', label: 'Step-by-step form', Icon: ListCheck },
];

export function studioMode(param, fallback = AI_STUDIO_DEFAULT_MODE) {
  if (STUDIO_MODES.some(m => m.key === param)) return param;
  return STUDIO_MODES.some(m => m.key === fallback) ? fallback : 'form';
}

export default function AIBulkStudioPage() {
  const { jobId } = useParams();
  const [params, setParams] = useSearchParams();
  const mode = studioMode(params.get('mode'));
  const showChat = !jobId && mode === 'chat';
  const [prefill] = useState(() => (jobId ? null : peekStudioPrefill()));
  useEffect(() => { if (prefill) forgetStudioPrefill(); }, [prefill]);

  // The content always sits in the same slot, so the form page keeps its
  // state when it moves itself from /bulk to /bulk/:jobId after generating.
  return (
    <>
      {!jobId && <ModeSwitch mode={mode} onChange={m => setParams({ mode: m })} maxWidth={showChat ? 760 : 920} />}
      {showChat ? <AIBulkChatPage prefill={prefill} /> : <AIBulkCreatePage prefill={prefill} />}
    </>
  );
}

function ModeSwitch({ mode, onChange, maxWidth }) {
  return (
    <div style={{ maxWidth, margin: '0 auto 16px', display: 'flex', justifyContent: 'flex-end' }}>
      <div role='group' aria-label='How do you want to create your posts?'
        style={{ display: 'inline-flex', gap: 4, padding: 4, background: '#f3f4f6', borderRadius: 10 }}>
        {STUDIO_MODES.map(({ key, label, Icon }) => {
          const on = mode === key;
          return (
            <button key={key} type='button' aria-pressed={on} onClick={() => { if (!on) onChange(key); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: 'none',
                fontSize: 13, fontWeight: 600, cursor: on ? 'default' : 'pointer',
                background: on ? '#fff' : 'transparent', color: on ? '#4f46e5' : '#6b7280',
                boxShadow: on ? '0 1px 2px rgba(17,24,39,0.08)' : 'none' }}>
              <Icon size={14} /> {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
